import React, { useContext, useState } from 'react'

import { useQuery } from '@apollo/client'

import { Link } from 'tools/Link'
import { MaxViewPage } from 'tools/Uniform'
import { freeze } from 'utils/dom'
import { cleanPath } from 'utils/string'
import { secondsForHumans } from 'utils/time'

import paths from 'common/paths'
import { doesOrgHaveAddress, normalizeOrg } from 'hub/GetStarted/reducer'
import GlobalContext from 'reducer/global'
import { usePage } from 'reducer/global/hooks'

import { BELL } from './graphql'

const defaultState = freeze({
  state: undefined,
  isActive: undefined,
  isMissingAddress: undefined,
  isMissingCreditCard: undefined,
  isMissingSshKey: undefined,
  passwordExpiresSoon: undefined
})

export function useMessages() {
  const [{ user, org }] = useContext(GlobalContext)
  const [state, setState] = useState(defaultState)

  useQuery(BELL, {
    variables: {
      orgId: org?.id
    },
    fetchPolicy: 'cache-and-network',
    skip: !org?.id,
    onCompleted({ orgs }) {
      let org = orgs?.results?.[0]
      if (org && orgs.success) {
        const isSelfService = org.class === 'self'
        const sshKeys = org.sshKeys
        const payment = org.data.find(({ type }) => type === 'payment')?.value
          ?.stripe?.active_id
        org = normalizeOrg(org)
        const hasAddress = doesOrgHaveAddress(org)
        setState({
          state: org.state,
          isSelfService,
          isActive: org.state === 'active',
          isMissingCreditCard: !payment,
          isMissingAddress: !hasAddress,
          isMissingSshKey: sshKeys.length === 0,
          passwordExpiresSoon: user.passwordExpiresSoon
        })
      }
    },
    onError(err) {
      console.error(err)
    }
  })

  return state
}

export function MessagesBell() {
  const [{ user }] = useContext(GlobalContext)
  const {
    passwordExpiresSoon,
    isMissingAddress,
    isMissingCreditCard,
    isActive,
    isSelfService
  } = useMessages()

  const showBell =
    user.isIdentified &&
    isSelfService &&
    (!isActive || passwordExpiresSoon || isMissingAddress || isMissingCreditCard)

  return (
    showBell && (
      <Link to={paths.public.messages}>
        <i className="fa fa-bell" style={{ color: 'var(--warn)' }} />
      </Link>
    )
  )
}

function Messages() {
  const [{ user, org }] = useContext(GlobalContext)
  usePage({ section: 'Messages', title: 'Messages' }, [])

  const {
    state,
    isActive,
    passwordExpiresSoon,
    isMissingAddress,
    isMissingCreditCard
  } = useMessages()

  const now = Date.now() / 1000
  const password = user.factors.find(
    ({ type, expiresAt }) => type === 'password' && expiresAt > now
  )

  return (
    <MaxViewPage>
      <ol>
        {passwordExpiresSoon && (
          <li>
            <div>
              Your password is going to expire in{' '}
              {secondsForHumans(password.expiresAt - now)}
            </div>
            <Link className="db pt2" to={'user/self/password'}>
              Change Password
            </Link>
          </li>
        )}
        {isMissingCreditCard && (
          <li>
            <div>
              Org <i>"{org.name}"</i> doesn't have a credit card on file:{' '}
              <Link to={cleanPath(paths.public.org, org.shortId, 'billing')}>
                Add Credit Card
              </Link>
            </div>
          </li>
        )}
        {isMissingAddress && (
          <li>
            <div>
              Org <i>"{org.name}"</i> is missing a company address:{' '}
              <Link to={cleanPath(paths.public.org, org.shortId, 'settings')}>
                Edit Org
              </Link>
            </div>
          </li>
        )}
        {!isActive && (
          <li>
            <StateWarning state={state} />
          </li>
        )}
      </ol>
    </MaxViewPage>
  )
}

function StateWarning({ state }) {
  const baseMsg = (
    <>
      Org State is <span className="warn">{state}</span> but must be{' '}
      <span className="warn">active</span> before servers can be added.
    </>
  )

  if (state === 'preparing') {
    return (
      <div>
        <p>{baseMsg}</p>
        <p>
          This might mean that the org owner has not verified their email. If you
          are the owner you can check this in your{' '}
          <Link to={cleanPath(paths.public.user, 'self', 'profile')}>
            user profile
          </Link>
          .
        </p>
      </div>
    )
  }

  return <div>{baseMsg}</div>
}

export default Messages
